import { ref, shallowRef, computed, watch, provide } from 'vue';
import type { Ref } from 'vue';
import { useRoute } from 'vue-router';

import OLMap from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import type MapBrowserEvent from 'ol/MapBrowserEvent';

import Consts from '@/api/Consts';

import { useMeasure } from '@/components/common/MeasureUtils';

import { useBaseStationsStore } from '@/stores/baseStations';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useUserStore } from '@/stores/user';
import type { GnssTooltipOverlayDisplayItem } from '@/stores/types/mapStoreContracts';
import { useMapFeaturesStore } from '@/stores/mapFeaturesStore';

import { useMapData } from '@/components/map/useMapData';
import { useProcessClickEvent } from '@/components/map/events/useProcessClickEvent';
import { useProcessHoverEvent } from '@/components/map/events/useProcessHoverEvent';
import {
	useAllowedClickTypes,
	useAllowedHoverTypes,
} from '@/components/map/features/useMapAllowedFeatureTypes';
import { useHoveredFeature } from '@/components/map/composables/useHoveredFeature';

import { useMapDataCCM } from '@/components/map/cnetConstructorMode/useMapData';
import type { MapRouteSnapshot } from '@/components/map/cnetConstructorMode/types/ccmTypes';
import { useMapContainerCCM } from '@/components/map/cnetConstructorMode/useMapContainer';
import { applyRouteMapMode } from '@/components/map/useMapRouteMode';

import { useLicenseStore } from '@/stores/licenseStore';

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { useMapLifecycle } from '@/components/map/events/useMapLifecycle';
import { useMapSourceWatchers } from '@/components/map/events/useMapSourceWatchers';
import { fitView, fitViewToRoversHistoryPaths } from '@/components/map/composables/mapOperations';
import { getGnssFeatures } from '@/components/map/features/mapTooltipBuilders';
import type { MapFeature } from '@/components/map/features/MapFeatures';
import MapHelpers from '@/components/map/utils/MapHelpers';
import { useDebouncedUpdateSource } from '@/components/map/composables/useDebouncedUpdateSource';
import { useMapZoom } from '@/components/map/events/useMapZoom';
import { useMapTooltip } from '@/components/map/composables/useMapTooltip';
import { collectMapSourceFeatures } from '@/components/map/features/useMapSourceFeatureGroups';
import type { ActiveSubNetworkWithColor } from '@/components/map/types/mapFeatureTypes';
import type { BsFilterSelected } from '@/components/map/features/useMapSourceFeatureGroups';
import { useMapSourceContext } from '@/components/map/features/useMapSourceContext';

import { asString } from '@/utils/typeGuards';


const useMapContainer = (
	mapRoot: Ref<HTMLElement | null | undefined>,
	gnssSelected: Ref<string>,
	bsFilterSelectedRef: Ref<BsFilterSelected>,
	subNetworkFilterSelectedRef: Ref<number>,
	baseStationsInSNRef: Ref<number[]>,
) => {
	const {
		featureData,
		displayOptions,
		interaction,
		tooltips,
	} = useMapData(gnssSelected, bsFilterSelectedRef, subNetworkFilterSelectedRef, baseStationsInSNRef);

	const mapDataCCM = useMapDataCCM();

	const baseStationsStore = useBaseStationsStore();
	const licenseStore = useLicenseStore();
	const userStore = useUserStore();
	const mapFeaturesStore = useMapFeaturesStore();
	const subNetworksStore = useSubNetworksStore();
	const activeSubNetworks = computed((): ActiveSubNetworkWithColor[] => subNetworksStore.subNetworks
		.map((item, index) => ({
			id: item.id,
			title: item.title,
			color: Consts.getColor(index, Consts.CHART_COLORS),
		}))
	);

	const olMap = shallowRef<OLMap | null>(null);
	const vectorLayer = shallowRef<VectorLayer<VectorSource> | null>(null);
	const tileLayer = shallowRef<TileLayer<OSM> | null>(null);

	const focusedMountpoint = ref('');
	const route = useRoute();
	const updateFocusedMountpoint = (): void => {
		focusedMountpoint.value = asString(route.params.focusedMountpoint);
	};
	updateFocusedMountpoint();

	const baseStationFeatures = shallowRef<MapFeature[]>([]);
	const roversHistoryPathsFeatures = shallowRef<MapFeature[]>([]);

	watch((): MapRouteSnapshot => ({ name: asString(route.name), params: route.params }), (to, from) => {
		mapDataCCM.handleRouteChange(to, from);
		applyRouteMapMode(to.name, from?.name, baseStationsStore, licenseStore);
		if (to.params.focusedMountpoint && to.params.focusedMountpoint !== from?.params.focusedMountpoint) {
			subNetworkFilterSelectedRef.value = 0;
			updateFocusedMountpoint();
			fitView(
				olMap.value,
				vectorLayer.value,
				focusedMountpoint.value,
				baseStationFeatures.value,
				baseStationsStore.mode,
				roversHistoryPathsFeatures.value,
				userStore.maxZoom,
			);
		}
	}, { immediate: true });

	watch(() => licenseStore.featureFlagDashboard, value => {
		if (value && route.name === 'rovers') {
			baseStationsStore.setMode(MAP_MODE.roversHistory);
		}
	}, { once: true });

	watch(featureData.roversHistoryPaths, (to, from) => {
		if (to.length > 0 && from.length === 0) {
			fitViewToRoversHistoryPaths(
				olMap.value,
				roversHistoryPathsFeatures.value,
				userStore.maxZoom,
			);
		}
	});

	const allowedHoverTypes = useAllowedHoverTypes(baseStationsStore, mapDataCCM.drawMode);
	const allowedClickTypes = useAllowedClickTypes(baseStationsStore);
	const { hovered, hoveredTyped, hoveredFeatureData, hoveredFeatureType, isMapHovered } = useHoveredFeature(allowedClickTypes);

	const buildSourceContext = useMapSourceContext({
		featureData,
		displayOptions,
		bsFilterSelectedRef,
		focusedMountpoint,
		hoveredFeatureData,
		activeSubNetworks,
		baseStationsStore,
		userStore,
		mapDataCCM,
		baseStationFeatures,
		roversHistoryPathsFeatures,
	});

	const updateSource = (doFit: boolean): void => {
		const mapInstance = olMap.value;
		const layer = vectorLayer.value;
		const tile = tileLayer.value;
		if (mapInstance == null || layer == null || tile == null) {
			return;
		}
		const source = layer.getSource();
		if (source == null) {
			return;
		}
		source.clear();

		source.addFeatures(collectMapSourceFeatures(buildSourceContext(doFit, mapInstance)));

		tile.setOpacity(baseStationsStore.mode === MAP_MODE.cnetConstructor ? 0.3 : 1);

		const view = mapInstance.getView();
		view.setMaxZoom(userStore.maxZoom);
		if (doFit && featureData.baseStations.value.length > 0) {
			fitView(
				mapInstance,
				layer,
				focusedMountpoint.value,
				baseStationFeatures.value,
				baseStationsStore.mode,
				roversHistoryPathsFeatures.value,
				userStore.maxZoom,
			);
		}
	};

	const debouncedUpdateSource = useDebouncedUpdateSource(updateSource);

	const { measureSource } = useMeasure(olMap, displayOptions.measureEnabled);

	const updateGnssFeatures = (): void => {
		mapFeaturesStore.updateFeatures(getGnssFeatures(olMap.value, vectorLayer.value));
	};

	const { tooltipStyle, updateTooltipCoords } = useMapTooltip(olMap);

	const { onHover, unhoverAnyThatIsHovered } = useProcessHoverEvent(
		olMap,
		hovered,
		vectorLayer,
		updateTooltipCoords,
		allowedHoverTypes,
		focusedMountpoint,
		displayOptions.fillCoverageArea,
	);
	provide('unhoverMapFeatures', unhoverAnyThatIsHovered);
	const { onClickMainMapModes } = useProcessClickEvent(
		olMap,
		interaction.mapClickEnabled,
		allowedClickTypes,
		unhoverAnyThatIsHovered,
	);

	const {
		sourceCCM,
		onClickCnetConstructorMapMode,
		addInteractions,
	} = useMapContainerCCM(mapDataCCM, debouncedUpdateSource, olMap);

	const onClick = (event: MapBrowserEvent<UIEvent>): void => {
		if (mapDataCCM.enabled.value) {
			onClickCnetConstructorMapMode(event);
		} else {
			onClickMainMapModes(event);
		}
	};

	const onZoom = useMapZoom(olMap);
	const onMoveEnd = (): void => {
		if (baseStationsStore.mode === MAP_MODE.roversHistory) {
			debouncedUpdateSource(false);
		}
	};

	useMapLifecycle({
		mapRoot,
		maxZoom: userStore.maxZoom,
		updateBSData: () => baseStationsStore.updateBSData(),
		olMap,
		vectorLayer,
		tileLayer,
		sourceCCM,
		measureSource,
		debouncedUpdateSource,
		onClick,
		updateGnssFeatures,
		onHover,
		onZoom,
		onMoveEnd,
		ccmEnabled: mapDataCCM.enabled,
		ccmDrawMode: mapDataCCM.drawMode,
		addInteractions,
	});

	useMapSourceWatchers({
		featureData,
		displayOptions,
		debouncedUpdateSource,
	});

	const gnssTooltipsOverlayDisplayItemsWithFocused = computed((): GnssTooltipOverlayDisplayItem[] => (
		tooltips.gnssTooltipsOverlayDisplayItems.value.map(item => ({
			...item,
			isFocused: MapHelpers.checkIsFocusedMountpoint(item.featureData, focusedMountpoint.value),
		}))
	));

	return {
		showGridpointsOnly: displayOptions.showGridpointsOnly,
		measureEnabled: displayOptions.measureEnabled,
		fillCoverageArea: displayOptions.fillCoverageArea,
		showBaseStationTooltips: displayOptions.showBaseStationTooltips,
		qualityColorMode: displayOptions.qualityColorMode,
		mapHoverPopupEnabled: interaction.mapHoverPopupEnabled,
		gnssTooltipsOverlayDisplayItems: gnssTooltipsOverlayDisplayItemsWithFocused,
		gnssTooltipsNonNullGnss: tooltips.gnssTooltipsNonNullGnss,
		isMapHovered,
		hoveredTyped,
		hoveredFeatureData,
		hoveredFeatureType,
		tooltipStyle,
		activeSubNetworks,
		unhoverAnyThatIsHovered,

		mapDataCCM,
		allSubnetworksMode: displayOptions.allSubnetworksMode,
		vrsAccuracySummary: tooltips.vrsAccuracySummary,
	};
};

export {
	useMapContainer,
};
